import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormHelperText,
  Typography,
  useTheme,
} from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../../modals/AnimatedModal";
import { StyledTextField } from "../../styled/inputs/textInput";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";
import {
  createDeliveryService,
  editDeliveryService,
  getDeliveryServiceById,
} from "../../../api/DeliveryService";

import { phoneMask } from "../../../utils/formatters";

export default function DeliveryServiceCreateEdit({
  id,
  show,
  closeModal,
  updateDeliveryServices,
}) {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const [form, setForm] = useState({
    name: "",
    phone: "",
  });

  useEffect(() => {
    if (id) {
      loadDeliveryService();
    }
  }, [id]);

  const loadDeliveryService = async () => {
    try {
      setIsLoading(true);
      const { data } = await getDeliveryServiceById(id);
      setForm({
        name: data.name || "",
        phone: data.phone || "",
      });
    } catch (error) {
      console.error("Error loading delivery service:", error);
      dispatchSnackBarMessage(
        "service-load-error",
        "Erro ao carregar serviço",
        "error"
      );
      closeModal();
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Apply phone mask if the field is phone
    let processedValue = value;
    if (name === "phone") {
      processedValue = phoneMask(value);
    }

    setForm({
      ...form,
      [name]: processedValue,
    });

    // Clear error when field is changed
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: "",
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!form.name.trim()) {
      errors.name = "Nome é obrigatório";
    }

    if (!form.phone) {
      errors.phone = "Telefone é obrigatório";
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormSubmitting(true);

    try {
      if (id) {
        await editDeliveryService(form, id);
        dispatchSnackBarMessage(
          "service-edit-success",
          "Serviço atualizado com sucesso",
          "success"
        );
      } else {
        await createDeliveryService(form);
        dispatchSnackBarMessage(
          "service-create-success",
          "Serviço criado com sucesso",
          "success"
        );
      }
      updateDeliveryServices();
      closeModal();
    } catch (error) {
      console.error("Error submitting form:", error);
      dispatchSnackBarMessage(
        "service-save-error",
        "Erro ao salvar serviço",
        "error"
      );
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <AnimatedModal
      isOpen={show}
      onClose={closeModal}
      width="500px"
      maxWidth="90%"
    >
      <ModalHeader
        title={id ? "Editar Serviço de Entrega" : "Novo Serviço de Entrega"}
        icon={LocalShippingIcon}
        onClose={closeModal}
      />

      <ModalContent>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ px: 3, pt: 2, pb: 4 }}
        >
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                  Nome do Serviço
                </Typography>
                <StyledTextField
                  fullWidth
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Digite o nome do serviço"
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  disabled={formSubmitting}
                  labelpadding="0 8px"
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                  Telefone
                </Typography>
                <StyledTextField
                  fullWidth
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="Digite o telefone"
                  error={!!formErrors.phone}
                  helperText={formErrors.phone}
                  disabled={formSubmitting}
                  labelpadding="0 8px"
                />
                <FormHelperText>Ex: (99) 99999-9999</FormHelperText>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={closeModal}
                  disabled={formSubmitting}
                  sx={{
                    borderRadius: 2,
                    py: 1,
                  }}
                >
                  Cancelar
                </Button>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={formSubmitting}
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      px: 3,
                      position: "relative",
                    }}
                  >
                    {formSubmitting && (
                      <CircularProgress
                        size={24}
                        sx={{
                          position: "absolute",
                          left: "50%",
                          top: "50%",
                          marginLeft: "-12px",
                          marginTop: "-12px",
                        }}
                      />
                    )}
                    <span style={{ opacity: formSubmitting ? 0 : 1 }}>
                      {id ? "Atualizar" : "Salvar"}
                    </span>
                  </Button>
                </motion.div>
              </Box>
            </>
          )}
        </Box>
      </ModalContent>
    </AnimatedModal>
  );
}
